import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, GitCompare, Phone } from "lucide-react";
import { useState, useEffect } from "react";
import { LOGO_URL, CONTACT } from "@/lib/constants";

const NAV = [
    { to: "/", label: "Home", end: true },
    { to: "/reviews", label: "Reviews" },
    { to: "/compare", label: "Compare" },
    { to: "/blog", label: "Blog" },
    { to: "/faq", label: "FAQ" },
    { to: "/security", label: "Security" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
];

export default function Header() {
    const [open, setOpen] = useState(false);
    const loc = useLocation();
    useEffect(() => { setOpen(false); }, [loc.pathname]);

    return (
        <div data-testid="site-header-wrapper">
            {/* Announcement bar with phone */}
            <div className="hidden md:block bg-zinc-900 border-b border-zinc-800" data-testid="announcement-bar">
                <div className="container-tight h-9 flex items-center justify-between text-xs">
                    <div className="text-zinc-400 tracking-wide">
                        Independent printer reviews · Editorially independent · Est. 2025
                    </div>
                    <div className="flex items-center gap-5">
                        <span className="text-zinc-500">{CONTACT.hours}</span>
                        <a
                            href={CONTACT.phoneHref}
                            data-testid="topbar-phone"
                            className="inline-flex items-center gap-1.5 text-white hover:text-blue-400 transition-colors font-medium"
                        >
                            <Phone className="h-3 w-3" /> {CONTACT.phoneDisplay}
                        </a>
                    </div>
                </div>
            </div>

            <header
                data-testid="site-header"
                className="sticky top-0 z-40 backdrop-blur-xl bg-zinc-950/85 border-b border-zinc-800"
            >
                <div className="container-tight flex items-center justify-between h-16 md:h-20">
                    <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
                        <div className="h-10 w-10 md:h-11 md:w-11 rounded-sm overflow-hidden bg-white p-0.5 ring-1 ring-zinc-800 group-hover:ring-blue-500 transition-colors duration-200">
                            <img src={LOGO_URL} alt="All About Printers" className="h-full w-full object-contain" />
                        </div>
                        <div className="hidden sm:flex flex-col leading-none">
                            <span className="font-display font-bold text-base tracking-tight text-white">All About Printers</span>
                            <span className="text-[10px] tracking-[0.22em] uppercase text-zinc-500 mt-1">Reviews · Compare · Guides</span>
                        </div>
                    </Link>

                    <nav className="hidden lg:flex items-center gap-1" data-testid="primary-nav">
                        {NAV.map(item => (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                data-testid={`nav-${item.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `px-3.5 py-2 text-sm font-medium rounded-sm transition-colors duration-200 ${
                                        isActive
                                            ? "text-white bg-zinc-900 border border-zinc-800"
                                            : "text-zinc-400 hover:text-white hover:bg-zinc-900/60"
                                    }`
                                }
                            >
                                {item.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="hidden lg:flex items-center gap-2">
                        <a
                            href={CONTACT.phoneHref}
                            data-testid="header-phone-cta"
                            className="btn-ghost text-sm"
                        >
                            <Phone className="h-4 w-4" /> {CONTACT.phoneDisplay}
                        </a>
                        <Link to="/compare" data-testid="compare-cta" className="btn-primary text-sm">
                            <GitCompare className="h-4 w-4" /> Compare
                        </Link>
                    </div>

                    <button
                        data-testid="mobile-menu-toggle"
                        onClick={() => setOpen(v => !v)}
                        className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-sm border border-zinc-800 text-zinc-300 hover:text-white hover:border-zinc-600 transition-colors"
                        aria-label="Toggle menu"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </div>

                {open && (
                    <div className="lg:hidden border-t border-zinc-800 bg-zinc-950" data-testid="mobile-nav">
                        <div className="container-tight py-4 flex flex-col gap-1">
                            {NAV.map(item => (
                                <NavLink
                                    key={item.to}
                                    to={item.to}
                                    end={item.end}
                                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                                    className={({ isActive }) =>
                                        `px-3 py-3 text-sm font-medium rounded-sm ${
                                            isActive ? "text-white bg-zinc-900" : "text-zinc-400"
                                        }`
                                    }
                                >
                                    {item.label}
                                </NavLink>
                            ))}
                            <a
                                href={CONTACT.phoneHref}
                                data-testid="mobile-phone-cta"
                                className="btn-primary mt-2 justify-center"
                            >
                                <Phone className="h-4 w-4" /> Call {CONTACT.phoneDisplay}
                            </a>
                        </div>
                    </div>
                )}
            </header>
        </div>
    );
}
